import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { USAMap } from "@/components/USAMap";
import { STATES_DATA } from "@/data/states";
import { ogImageMeta } from "@/lib/og-image";

export const Route = createFileRoute("/coverage")({
  head: () => ({
    meta: [
      { title: "Free Vending Machines USA — Nationwide Coverage Map" },
      {
        name: "description",
        content:
          "Interactive USA map. Click any state to get FREE vending machine placement, install, restocking & service in your city. Zero cost — nationwide coverage.",
      },
      {
        property: "og:title",
        content: "Free Vending Machines USA — Nationwide Coverage Map",
      },
      {
        property: "og:description",
        content:
          "Click your state on the interactive map for FREE vending machine placement and service in your city. Zero-cost install across the USA.",
      },
      { property: "og:url", content: "https://vendingmachinefree.com/coverage" },
      { property: "og:type", content: "website" },
      ...ogImageMeta(),
    ],
    links: [{ rel: "canonical", href: "https://vendingmachinefree.com/coverage" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
            { "@type": "ListItem", position: 2, name: "Nationwide Coverage", item: "https://vendingmachinefree.com/coverage" },
          ],
        }),
      },
    ],
  }),
  component: CoveragePage,
});

function CoveragePage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link
            to="/"
            hash="contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="py-16 md:py-20 bg-charcoal text-charcoal-foreground">
        <div className="container-x text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <MapPin className="h-4 w-4" /> All 50 States · Active Operators
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase max-w-3xl mx-auto">
            Free Vending Machines — USA Nationwide Coverage
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto text-lg">
            We provide FREE vending machine placement, installation, restocking, and maintenance
            across all 50 states.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x">
          <USAMap />
          <div className="mt-12 text-center">
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
            >
              Get FREE Vending Machines — USA Nationwide Services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase text-center">
            Top Metro Areas — FREE Vending Placement
          </h2>
          <p className="mt-3 text-center text-muted-foreground max-w-2xl mx-auto">
            Highest-demand cities we install in every week. Click your metro for local coverage details.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {[
              ["california","Los Angeles"],["california","San Francisco"],["california","San Diego"],
              ["new-york","New York City"],["texas","Houston"],["texas","Dallas"],["texas","Austin"],
              ["florida","Miami"],["florida","Orlando"],["florida","Tampa"],["illinois","Chicago"],
              ["pennsylvania","Philadelphia"],["arizona","Phoenix"],["georgia","Atlanta"],
              ["washington","Seattle"],["massachusetts","Boston"],["colorado","Denver"],
              ["nevada","Las Vegas"],["michigan","Detroit"],["ohio","Columbus"],
            ].map(([state, city]) => (
              <Link
                key={`${state}-${city}`}
                to="/states/$state/$town"
                params={{ state, town: city.toLowerCase().replace(/[^a-z0-9]+/g, "-") }}
                className="flex items-center gap-2 rounded-md bg-card border border-border px-3 py-2.5 text-sm hover:border-safety hover:text-safety transition"
              >
                <MapPin className="h-3.5 w-3.5 text-safety" />
                <span className="font-medium">{city}</span>
              </Link>
            ))}
          </div>

          <h2 className="mt-16 font-display text-3xl uppercase text-center">
            Browse All 50 State Coverage Pages
          </h2>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {STATES_DATA.slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((s) => (
                <Link
                  key={s.code}
                  to="/states/$state"
                  params={{ state: s.slug }}
                  className="flex items-center gap-2 rounded-md bg-card border border-border px-3 py-2.5 text-sm hover:border-safety hover:text-safety transition"
                >
                  <MapPin className="h-3.5 w-3.5 text-safety" />
                  <span className="font-medium">{s.name}</span>
                </Link>
              ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
            >
              Apply for FREE Vending Now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
